import { Icon } from "@iconify/react";
import JoditEditor from "jodit-react";
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import DeleteHook from "../../../Hooks/DeleteHook";
import PostHooks from "../../../Hooks/PostHooks";
import UpdateHooks from "../../../Hooks/UpdateHooks";
import { useQuery } from "../../../Hooks/useApi";

const SuperAdminResearch = () => {
  const [refetch, setRefetch] = useState(false);
  const { data: researchResponse } = useQuery("/research/getresearch");
  const researches = researchResponse?.data || [];
  const editor = useRef(null);
  const [formData, setFormData] = useState({
    researchTitle: "",
    research: "",
    researchersName: "",
    importantUrl: "",
  });

  function scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (formData?._id) {
        await UpdateHooks(
          `https://servercareseebd.careseebd.com/api/v1/research/updateResearch/${formData?._id}`,
          formData
        );
        toast.success("Research section updated successfully!");
        setFormData({ researchTitle: "", research: "", researchersName: "", importantUrl: "" });
        setRefetch(!refetch);
      } else {
        await PostHooks(
          `https://servercareseebd.careseebd.com/api/v1/research/addResearch`,
          formData,
          "Research posted successfully!"
        );
        setFormData({
          researchTitle: "",
          research: "",
          researchersName: "",
          importantUrl: "",
        });
        setTimeout(() => {
          setRefetch(!refetch);
        }, 1000);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="max-w-5xl mx-auto select-none space-y-6">
      {/* Header bar */}
      <div className="flex items-center gap-3 bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
        <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl">
          <Icon icon="solar:square-academic-cap-bold" className="w-6 h-6" />
        </div>
        <div>
          <h1 className="text-lg font-black text-slate-800 tracking-tight">Scientific Research Papers</h1>
          <p className="text-xs text-slate-400 font-semibold mt-0.5">Manage published research, medical case studies, and citations</p>
        </div>
      </div>

      {/* Form panel (Full Width) */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 lg:p-8 space-y-4">
        <h2 className="text-xs font-black text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
          <Icon icon="solar:document-bold" className="w-4 h-4 text-indigo-500" />
          {formData?._id ? "Edit Research Details" : "Add Research Entry"}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide">Researchers Name</label>
              <input
                type="text"
                name="researchersName"
                onChange={handleChange}
                value={formData.researchersName}
                required
                placeholder="Dr. Jane Smith, Dr. Alan Turing"
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-xs font-semibold"
              />
            </div>

            <div className="space-y-1.5">
              <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide">Research Title</label>
              <input
                type="text"
                name="researchTitle"
                onChange={handleChange}
                value={formData.researchTitle}
                required
                placeholder="e.g. Clinical Study on Patient Care Pathways"
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-xs font-semibold"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide">Citation/Study Content</label>
            <div className="rounded-xl overflow-hidden border border-slate-200">
              <JoditEditor
                ref={editor}
                value={formData?.research}
                onChange={(newContent) => {
                  setFormData((prev) => ({
                    ...prev,
                    research: newContent,
                  }));
                }}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide">Important Link (Optional)</label>
            <textarea
              name="importantUrl"
              onChange={handleChange}
              value={formData.importantUrl}
              rows="2"
              placeholder="URL link to the full publication..."
              className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-xs font-medium"
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            {formData?._id && (
              <button
                type="button"
                onClick={() => {
                  setFormData({ researchTitle: "", research: "", researchersName: "", importantUrl: "" });
                }}
                className="px-6 py-2.5 rounded-xl border border-slate-200 text-slate-500 font-extrabold text-xs transition-all hover:bg-slate-50 cursor-pointer"
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              className="px-6 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-600 hover:opacity-95 text-white font-extrabold text-xs rounded-xl shadow-md shadow-cyan-500/10 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer"
            >
              {formData?._id ? "Update Paper" : "Add Paper"}
            </button>
          </div>
        </form>
      </div>

      {/* List panel (Full Width) */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 lg:p-8 space-y-4">
        <h2 className="text-xs font-black text-slate-400 uppercase tracking-wider">Published Research Entries</h2>
        <div className="overflow-x-auto border border-slate-100 rounded-xl">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50/75 border-b border-slate-100">
                <th className="px-6 py-4 text-[10px] font-extrabold text-slate-500 uppercase tracking-wider">Researchers</th>
                <th className="px-6 py-4 text-[10px] font-extrabold text-slate-500 uppercase tracking-wider">Paper Title</th>
                <th className="px-6 py-4 text-[10px] font-extrabold text-slate-500 uppercase tracking-wider">Abstract Snippet</th>
                <th className="px-6 py-4 text-[10px] font-extrabold text-slate-500 uppercase tracking-wider text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {researches.length > 0 ? (
                researches.map((research) => (
                  <tr key={research?._id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-6 py-4 text-xs font-bold text-slate-800">
                      {research?.researchersName}
                    </td>
                    <td className="px-6 py-4 text-xs font-semibold text-indigo-650">
                      {research?.researchTitle}
                    </td>
                    <td className="px-6 py-4 text-xs font-medium text-slate-500 max-w-md">
                      <div
                        className="truncate"
                        dangerouslySetInnerHTML={{
                          __html: research?.research,
                        }}
                      />
                    </td>
                    <td className="px-6 py-4 text-xs">
                      <div className="flex justify-end items-center gap-2">
                        <button
                          onClick={async () => {
                            setFormData(research);
                            scrollToTop();
                          }}
                          className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 border border-slate-200 hover:border-indigo-150 rounded-lg transition-all cursor-pointer"
                          title="Edit Paper"
                        >
                          <Icon icon="basil:edit-outline" className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() =>
                            DeleteHook({
                              setRefetch,
                              refetch,
                              url: `https://servercareseebd.careseebd.com/api/v1/research/deleteResearch/${research?._id}`,
                            })
                          }
                          className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 border border-slate-200 hover:border-rose-150 rounded-lg transition-all cursor-pointer"
                          title="Delete Paper"
                        >
                          <Icon icon="material-symbols:delete-outline" className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="text-center py-10 text-xs text-slate-400">
                    No research papers added.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminResearch;
